import { useCallback, useState } from "react";
import { Flex, SlideOutDrawer, Tab, Tabs, Text } from "@jsq/ds/core";
import { ExitWarningModal } from "~/pages/Compliance/Common/ExitWarningModal";
import { ClientProgramTab } from "./ClientProgramTab";
import { EntityProgramTab } from "./EntityProgramTab";
import { InvestorProgramTab } from "./InvestorProgramTab";
import * as styles from "./styles_vanilla.css";
import {
  SettingsDrawerRow,
  SettingsDrawerSubject,
  SettingsDrawerTab,
  SettingsDrawerTabProps,
  TABS_BY_SUBJECT,
} from "./types";

export type ComplianceSettingsDrawerProps = {
  subject: SettingsDrawerSubject;
  /** The clicked grid row; the drawer is open while a row is set. */
  row: SettingsDrawerRow | undefined;
  onClose: () => void;
  /** Called after a tab saves successfully (e.g. to refresh the grid). */
  onSaved?: () => void;
  /** Tab shown on open. Falls back to the subject's first tab if omitted or not in `tabs`. */
  defaultTab?: SettingsDrawerTab;
};

const resolveInitialTab = (
  tabs: SettingsDrawerTab[],
  defaultTab?: SettingsDrawerTab,
): SettingsDrawerTab => (defaultTab && tabs.includes(defaultTab) ? defaultTab : tabs[0]);

const TAB_CONTENT: Record<
  SettingsDrawerTab,
  (props: SettingsDrawerTabProps) => JSX.Element | null
> = {
  [SettingsDrawerTab.InvestorProgram]: InvestorProgramTab,
  [SettingsDrawerTab.EntityProgram]: EntityProgramTab,
  [SettingsDrawerTab.ClientProgram]: ClientProgramTab,
};

/**
 * Row-click settings side panel, parameterized by subject (entity: Investor + Entity program
 * tabs; client: Client program tab). Uses the same `SlideOutDrawer` as the case-details drawer so
 * the three match (plain dim, not the blurred V2 overlay); unsaved changes are guarded on close.
 */
export const ComplianceSettingsDrawer = ({
  subject,
  row,
  onClose,
  onSaved,
  defaultTab,
}: ComplianceSettingsDrawerProps) => {
  const tabs = TABS_BY_SUBJECT[subject];
  const [activeTab, setActiveTab] = useState<SettingsDrawerTab>(() =>
    resolveInitialTab(tabs, defaultTab),
  );
  const [isDirty, setIsDirty] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  // Tab the user tried to switch to while dirty; applied if they confirm the discard
  const [pendingTab, setPendingTab] = useState<SettingsDrawerTab | undefined>(undefined);

  // Reset per-row state when a different row opens the drawer (render-phase adjustment,
  // per the React "storing information from previous renders" pattern)
  const rowKey = row ? `${subject}:${row.id}:${defaultTab ?? ""}` : undefined;
  const [lastRowKey, setLastRowKey] = useState(rowKey);
  if (row && rowKey !== lastRowKey) {
    setLastRowKey(rowKey);
    setActiveTab(resolveInitialTab(tabs, defaultTab));
    setIsDirty(false);
    setShowExitModal(false);
    setPendingTab(undefined);
  }

  const handleSaved = useCallback(() => {
    setIsDirty(false);
    onSaved?.();
    onClose();
  }, [onSaved, onClose]);

  const confirmDiscard = useCallback(() => {
    setShowExitModal(false);
    setIsDirty(false);
    if (pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(undefined);
    } else {
      onClose();
    }
  }, [pendingTab, onClose]);

  const cancelDiscard = useCallback(() => {
    setShowExitModal(false);
    setPendingTab(undefined);
  }, []);

  const requestClose = useCallback(() => {
    if (isDirty) {
      setShowExitModal(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const requestTabSwitch = useCallback(
    (tab: SettingsDrawerTab) => {
      if (tab === activeTab) return;
      if (isDirty) {
        setPendingTab(tab);
        setShowExitModal(true);
      } else {
        setActiveTab(tab);
      }
    },
    [activeTab, isDirty],
  );

  if (!row) return null;

  const TabContent = TAB_CONTENT[activeTab];

  return (
    <>
      <SlideOutDrawer
        anchor="right"
        drawerSize="fluid"
        shouldCloseOnContentInteraction={false}
        isShowing
        onClose={requestClose}
      >
        <Flex flexDirection="column" className={styles.container}>
          <Flex
            flexDirection="column"
            className={styles.headerBlock}
            data-testid="settings-drawer-header"
          >
            <Text variant="h4">{row.name}</Text>
            {subject === "entity" && row.arenaName && (
              <Text variant="secondary-sm">{row.arenaName}</Text>
            )}
          </Flex>
          <Tabs className={styles.tabs}>
            {tabs.map((tab) => (
              <Tab
                key={tab}
                data-testid={`settings-drawer-tab-${tab.toLowerCase().replace(/ /g, "-")}`}
                active={activeTab === tab}
                text={tab}
                onClick={() => requestTabSwitch(tab)}
              />
            ))}
          </Tabs>
          <div className={styles.tabContent}>
            <TabContent
              row={row}
              onDirtyChange={setIsDirty}
              onSaved={handleSaved}
              onCancel={requestClose}
            />
          </div>
        </Flex>
      </SlideOutDrawer>
      <ExitWarningModal
        show={showExitModal}
        onCancel={cancelDiscard}
        onConfirm={confirmDiscard}
        title="Exit and discard"
        message="Are you sure you want to exit and discard the changes? This action cannot be undone."
        confirmLabel="Exit and discard"
      />
    </>
  );
};
